import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Brain, Sparkles, Lightbulb, MessageSquare, Infinity, Link2, Image as ImageIcon, Users, Smile, MapPin, Film, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import MarchingAntsUpload from '@/components/bulletins/MarchingAntsUpload';
import CustomTextarea from '@/components/bulletins/CustomTextarea';
import { MentionInput } from '@/components/mentions/MentionInput';
import Portal from '@/components/ui/portal';
import PostUploadProgress from './PostUploadProgress';
import { useContextEngine } from '@/hooks/use-context-engine';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useRealtimeCommunityExtraction } from '@/hooks/use-realtime-community-extraction';
import CommunityPreview from './CommunityPreview';
import { validateVideoForLoop, formatDuration } from '@/utils/video-helpers';
import { toast } from 'sonner';

interface PostPromptModalProps {
  open: boolean;
  title?: string;
  description?: string;
  onClose: () => void;
  // Return separate values: postCopy (Loops), mediaTitle, mediaDescription, optional image, optional video, isArt flag, and disabled communities
  onSubmit: (postCopy: string, mediaTitle: string, mediaDescription: string, image?: File | null, isArt?: boolean, disabledCommunityIds?: Set<string>, video?: File | null) => void;
  uploadProgress?: { progress: number; state: string };
  isPremium?: boolean;
  isAdmin?: boolean;
}

type ModalStep = 'selectImage' | 'addDetails' | 'uploading';

const PostPromptModal: React.FC<PostPromptModalProps> = ({
  open,
  onClose,
  onSubmit,
  uploadProgress,
  title = '',
  description = '',
  isPremium = false,
  isAdmin = false,
}) => {
  const [step, setStep] = useState<ModalStep>('addDetails');
  const [postTitle, setPostTitle] = useState(title);
  // Post copy (Loops) - start empty, not with description
  const [postCopy, setPostCopy] = useState('');
  // Media description (Bulletins)
  const [postDescription, setPostDescription] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [showVideoBanner, setShowVideoBanner] = useState(false);
  const textareaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  
  // Context engine for enhanced post creation
  const {
    processExpression,
    debouncedProcessExpression,
    isSearching: isAnalyzing,
    contextAnalysis,
    results: contextEngineResults
  } = useContextEngine();
  
  // Community extraction for real-time community prediction
  const {
    predictedCommunities,
    isExtracting: isExtractingCommunities,
    hasPredictions
  } = useRealtimeCommunityExtraction(postCopy);
  
  // Suggested tags based on content analysis
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showContextTooltip, setShowContextTooltip] = useState(false);
  const [openIconTooltip, setOpenIconTooltip] = useState<string | null>(null);
  const [isArt, setIsArt] = useState(false); // Default to false to prevent accidental artstream posts
  const [disabledCommunities, setDisabledCommunities] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setStep('addDetails');
      setPostTitle(title);
      setPostCopy(''); // Always start with empty text
      setPostDescription('');
      setSelectedImage(null);
      setSelectedVideo(null);
      setVideoDuration(null);
      setShowVideoBanner(false);
      setDisabledCommunities(new Set()); // Reset disabled communities
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(null);
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
      setVideoPreview(null);
    }
  }, [open, title]); // Removed description dependency since we're not using it for initial value
  
  const handleToggleCommunity = (communityId: string) => {
    setDisabledCommunities(prev => {
      const next = new Set(prev);
      if (next.has(communityId)) {
        next.delete(communityId);
      } else {
        next.add(communityId);
      }

      console.log('[PostPromptModal] Toggled community prediction state:', {
        communityId,
        isNowDisabled: next.has(communityId),
        disabledCount: next.size,
        disabledIds: Array.from(next),
      });
      return next;
    });
  };

  useEffect(() => {
    if (step === 'addDetails' && open) {
      console.log('Moving to addDetails step, imagePreview:', imagePreview);
      console.log('Selected image:', selectedImage?.name);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [step, open, imagePreview, selectedImage]);
  
  // Process post content (Loops copy) through the context engine
  useEffect(() => {
    if (postCopy.length > 10 && step === 'addDetails') {
      debouncedProcessExpression(postCopy);
    }
  }, [postCopy, debouncedProcessExpression, step]);
  
  // Generate suggested tags when context analysis changes
  useEffect(() => {
    if (contextAnalysis?.interests && contextAnalysis.interests.length > 0) {
      const tags = contextAnalysis.interests
        .map(interest => interest.toLowerCase().replace(/[^a-z0-9]/g, ''))
        .filter(tag => tag.length > 0);
      
      // Add emotional state as a tag if available
      if (contextAnalysis.emotionalState) {
        const emotionTag = contextAnalysis.emotionalState.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (emotionTag && !tags.includes(emotionTag)) {
          tags.unshift(emotionTag);
        }
      }
      
      setSuggestedTags(tags.slice(0, 5)); // Limit to 5 tags
      
      // Show context tooltip briefly
      setShowContextTooltip(true);
      setTimeout(() => setShowContextTooltip(false), 3000);
    }
  }, [contextAnalysis]);

  const handleImageSelect = (file: File) => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    const newPreviewUrl = URL.createObjectURL(file);
    setSelectedImage(file);
    setImagePreview(newPreviewUrl);
    setStep('addDetails');
    // Show video banner after image is selected
    setShowVideoBanner(true);
  };
  
  const handleVideoSelect = async (file: File) => {
    console.log('[PostPromptModal] Video file selected:', file.name);
    
    // Validate video
    const validation = await validateVideoForLoop(file);
    
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid video file');
      return;
    }
    
    // Video is valid, set it
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    const newPreviewUrl = URL.createObjectURL(file);
    setSelectedVideo(file);
    setVideoPreview(newPreviewUrl);
    setVideoDuration(validation.duration || null);
    setShowVideoBanner(false); // Hide banner after video selected
    
    toast.success(`Video added (${formatDuration(validation.duration || 0)})`);
  };
  
  const handleRemoveVideo = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setSelectedVideo(null);
    setVideoPreview(null);
    setVideoDuration(null);
    setShowVideoBanner(true); // Show banner again if they remove video
  };
  
  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('File input change event triggered');
    const file = e.target.files?.[0];
    console.log('Selected file:', file);
    if (file) {
      // Create object URL for preview
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      const newPreviewUrl = URL.createObjectURL(file);
      console.log('Created new preview URL:', newPreviewUrl);
      
      // Update state directly here
      setSelectedImage(file);
      setImagePreview(newPreviewUrl);
      
      // Store in ref to ensure it's not lost during state updates
      const imageRef = newPreviewUrl;
      
      // Force move to next step with a small delay to ensure state updates are processed
      setTimeout(() => {
        console.log('Checking image preview before step change:', imageRef);
        if (!imagePreview && imageRef) {
          console.log('Image preview was lost, restoring from ref');
          setImagePreview(imageRef);
        }
        setStep('addDetails');
      }, 100);
    } else {
      console.log('No file selected');
    }
  };

  const handleTriggerUpload = () => {
    console.log('Trigger upload clicked');
    if (fileInputRef.current) {
      console.log('File input ref exists, clicking it');
      fileInputRef.current.click();
    } else {
      console.log('File input ref is null');
    }
  };

  const handleBack = () => {
    // If media is selected, clear it to allow choosing later; otherwise close
    if (selectedImage || imagePreview) {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      setSelectedImage(null);
      setImagePreview(null);
    } else {
      onClose();
    }
  };

  const handleSubmit = () => {
    // Allow submit if either text (postCopy or mediaDescription) or media is present
    const hasText = (postCopy?.trim().length || 0) > 0 || (postDescription?.trim().length || 0) > 0 || (postTitle?.trim().length || 0) > 0;
    if (!selectedImage && !hasText) return;
    
    console.log('[PostPromptModal] handleSubmit called with:', {
      isArt,
      disabledCommunitiesSize: disabledCommunities.size,
      disabledCommunitiesArray: Array.from(disabledCommunities),
      hasSelectedImage: !!selectedImage,
      hasSelectedVideo: !!selectedVideo
    });
    
    if (selectedImage) {
      setStep('uploading');
      onSubmit(postCopy, postTitle, postDescription, selectedImage, isArt, disabledCommunities, selectedVideo);
    } else {
      onSubmit(postCopy, '', postDescription, null, isArt, disabledCommunities, null);
      onClose();
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    onClose();
  };

  useEffect(() => {
    // Cleanup object URLs on unmount
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [imagePreview, videoPreview]);

  if (!open) {
    return null;
  }

  // Enable submit if either text (postCopy) or media is present
  const isSubmitDisabled = !selectedImage && !((postCopy?.trim().length || 0) > 0);

  const isVideoFile = (file: File | null) => {
    if (!file) return false;
    return file.type.startsWith('video/') || 
           file.name.toLowerCase().endsWith('.mp4') || 
           file.name.toLowerCase().endsWith('.webm') || 
           file.name.toLowerCase().endsWith('.mov');
  };

  const renderStep = () => {
    switch (step) {
      case 'selectImage':
        return (
          <>
            <header className="p-5 border-b border-[#343536] flex items-center justify-between bg-[#1A1A1B]">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-6 h-6 bg-purple-500 flex items-center justify-center rounded-full text-sm">1</span> 
                <span>Select Media</span>
              </h2>
              <Button variant="ghost" size="icon" onClick={handleClose} className="rounded-full hover:bg-white/10">
                <X className="h-5 w-5" />
              </Button>
            </header>
            <main className="flex-1 p-6 overflow-y-auto bg-[#1A1A1B] flex flex-col items-center justify-center">
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // Direct state updates to force step change
                    setSelectedImage(file);
                    const newPreviewUrl = URL.createObjectURL(file);
                    setImagePreview(newPreviewUrl);
                    
                    // Force the step change after a small delay
                    window.setTimeout(() => {
                      setStep('addDetails');
                    }, 100);
                  }
                }}
                accept={isPremium || isAdmin ? "image/jpeg,image/png,image/gif,video/mp4,video/quicktime" : "image/jpeg,image/png"}
                style={{ display: 'none' }} 
              />
              
              <div 
                className="w-full max-w-md h-[260px] bg-black/30 backdrop-blur rounded-2xl flex flex-col items-center justify-center p-6 border border-slate-700/30 relative overflow-hidden group transition-all duration-300"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add('ring-2', 'ring-purple-500', 'ring-opacity-70');
                  e.currentTarget.classList.add('border-purple-500/50');
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('ring-2', 'ring-purple-500', 'ring-opacity-70');
                  e.currentTarget.classList.remove('border-purple-500/50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('ring-2', 'ring-purple-500', 'ring-opacity-70');
                  e.currentTarget.classList.remove('border-purple-500/50');
                  
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    // Direct state updates and step change
                    setSelectedImage(file);
                    const newPreviewUrl = URL.createObjectURL(file);
                    setImagePreview(newPreviewUrl);
                    setStep('addDetails');
                  }
                }}
              >
                {/* Background animation */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/5 z-0 opacity-30"></div>
                <div className="absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-t from-black/20 to-transparent z-0"></div>
                
                {/* Dots pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
                </div>
                
                <div className="relative z-10 flex flex-col items-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-purple-500/20 p-0.5 group-hover:scale-105 transition-transform duration-300">
                    <div className="bg-black/40 backdrop-blur-sm w-full h-full rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                        <rect width="18" height="18" x="3" y="3" rx="2" />
                        <circle cx="9" cy="9" r="2" />
                        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Add Media</h3>
                  <p className="text-xs text-gray-300 text-center mb-5 max-w-xs">
                    Drag & drop your image or video here, or click the button below to browse files
                  </p>
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-3 rounded-lg shadow-lg shadow-purple-900/20 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-purple-800/30"
                  >
                    Select File
                  </Button>
                  <p className="text-xs text-gray-400 mt-5">
                    {isPremium || isAdmin ? (
                      <>
                        Supports JPG, PNG, <span className="text-purple-400">GIF, MP4</span> • Max 50MB
                        {!isPremium && isAdmin && <span className="ml-1 text-blue-400">(Admin)</span>}
                        {isPremium && <span className="ml-1 text-purple-400">(Premium)</span>}
                      </>
                    ) : (
                      <>
                        Supports JPG, PNG • Max 50MB
                        <span className="block mt-1 text-purple-400">
                          <span className="text-purple-300">✨</span> Upgrade to Premium for GIF and video uploads
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </main>
            <div className="mt-6 text-center max-w-md">
              <p className="text-xs text-gray-300/90">
                You can post media and text. Use the "Is it art?" toggle in the composer to feature posts in Artstream.
              </p>
            </div>
          </>
        );
      case 'addDetails':
        console.log('Rendering addDetails step, imagePreview:', imagePreview);
        console.log('Rendering addDetails step, selectedImage:', selectedImage?.name);
        return (
          <>
            <header className="p-5 border-b border-[#343536] bg-[#1A1A1B]">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span>Create post</span>
                </h2>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={handleClose} className="rounded-full hover:bg-white/10">
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </header>
            <main className="flex-1 p-6 overflow-y-auto bg-[#1A1A1B]">
              {/* Hidden file input for addDetails step */}
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileSelected}
                accept={isPremium || isAdmin ? "image/jpeg,image/png,image/gif,video/mp4,video/quicktime" : "image/jpeg,image/png"}
                style={{ display: 'none' }}
              />
              {/* Hidden video input */}
              <input 
                type="file" 
                ref={videoInputRef} 
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleVideoSelect(file);
                  }
                }}
                accept="video/mp4,video/webm,video/quicktime"
                style={{ display: 'none' }}
              />
              
              {/* Optional Video Upload Banner - shows after image selected */}
              {selectedImage && showVideoBanner && !selectedVideo && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mb-4 p-3 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-lg flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Film className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Add a BTS clip or video?</p>
                      <p className="text-xs text-gray-400">Max 12 seconds</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => videoInputRef.current?.click()}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Add Video
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowVideoBanner(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              )}
              
              {/* Video Preview - shows if video selected */}
              {selectedVideo && videoPreview && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 bg-black/40 border border-purple-500/50 rounded-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Film className="w-4 h-4 text-purple-400" />
                      <span className="text-sm font-medium text-purple-300">BTS Video</span>
                      {videoDuration && (
                        <span className="text-xs text-gray-400">({formatDuration(videoDuration)})</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleRemoveVideo}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <video 
                    src={videoPreview} 
                    className="w-full max-h-32 object-contain rounded bg-black"
                    controls
                  />
                  <p className="text-xs text-gray-400 mt-2 italic">This will appear as a flip-card on your post</p>
                </motion.div>
              )}
              
              {selectedImage ? (
                <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="relative group rounded-lg overflow-hidden w-full aspect-square bg-black flex items-center justify-center">
                    {isVideoFile(selectedImage) ? (
                      <video src={imagePreview || ''} className="w-full h-full object-contain rounded-lg" controls />
                    ) : (
                      <img src={imagePreview || ''} alt="Preview" className="w-full h-full object-contain rounded-lg" />
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-80 group-hover:opacity-100">
                      <p className="text-xs text-gray-300 truncate">{selectedImage?.name}</p>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <div className="relative">
                      <MentionInput
                        ref={textareaRef}
                        value={postCopy}
                        onChange={(value) => setPostCopy(value.slice(0, 500))}
                        placeholder={title || "What's on your mind?"}
                        maxLength={500}
                        className="w-full"
                        textareaClassName="min-h-[120px] bg-black/30 border-slate-700/50 focus-visible:ring-purple-500 rounded-xl py-5 px-5 sm:py-6 sm:px-6 text-white placeholder-gray-400 hover:border-slate-600 transition-colors w-full"
                      />
                      
                      {/* Community Preview */}
                      <CommunityPreview 
                        communities={predictedCommunities} 
                        isExtracting={isExtractingCommunities}
                        className="mt-2"
                        disabledCommunities={disabledCommunities}
                        onToggleCommunity={handleToggleCommunity}
                      />
                      {isAnalyzing && (
                        <div className="absolute top-2 right-2">
                          <div className="w-6 h-6 flex items-center justify-center bg-purple-900/40 rounded-full p-1">
                            <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                          </div>
                        </div>
                      )}
                      {contextAnalysis && !isAnalyzing && (
                        <TooltipProvider>
                          <Tooltip open={showContextTooltip} onOpenChange={setShowContextTooltip}>
                            <TooltipTrigger asChild>
                              <button 
                                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-purple-700/40 hover:bg-purple-700/60 rounded-full p-1 transition-colors"
                                type="button"
                                onClick={() => setShowContextTooltip(true)}
                              >
                                <Brain className="w-4 h-4 text-purple-300" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs bg-black/90 border-purple-500/30">
                              <div className="space-y-2 p-1">
                                <div className="flex items-center gap-2">
                                  <Sparkles className="w-4 h-4 text-purple-400" />
                                  <span className="text-sm font-medium text-purple-300">Context Analysis</span>
                                </div>
                                {contextAnalysis.emotionalState && (
                                  <div className="flex gap-1 flex-wrap">
                                    <span className="text-xs text-white/70">Emotional tone:</span>
                                    <span className="text-xs font-medium text-purple-300">{contextAnalysis.emotionalState}</span>
                                  </div>
                                )}
                                {contextAnalysis.interests && contextAnalysis.interests.length > 0 && (
                                  <div className="flex gap-1 flex-wrap">
                                    <span className="text-xs text-white/70">Key topics:</span>
                                    <span className="text-xs font-medium text-purple-300">{contextAnalysis.interests.slice(0, 3).join(', ')}</span>
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <div className="text-xs text-white/70">
                        {postCopy.length} / 500
                      </div>
                      {suggestedTags.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Lightbulb className="w-3 h-3 text-amber-400" />
                          <span className="text-xs text-amber-400/80">Suggested tags:</span>
                        </div>
                      )}
                    </div>
                    {suggestedTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {suggestedTags.map((tag) => (
                          <Badge 
                            key={tag}
                            variant={selectedTags.includes(tag) ? "default" : "outline"}
                            className={`cursor-pointer px-2 py-0.5 text-xs hover:bg-purple-700/70 ${selectedTags.includes(tag) ? 'bg-purple-700' : 'bg-transparent hover:bg-purple-900/30 border-purple-700/50 text-purple-300'} transition-colors`}
                            onClick={() => {
                              if (selectedTags.includes(tag)) {
                                setSelectedTags(selectedTags.filter(t => t !== tag));
                                setPostCopy(postCopy.replace(`#${tag}`, '').trim());
                              } else {
                                setSelectedTags([...selectedTags, tag]);
                                setPostCopy(`${postCopy} #${tag}`.trim());
                              }
                            }}
                          >
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 text-[11px] text-white/60">
                      • Posts can include media and text.
                      <br />• Use “Is it art?” to include or exclude the post from Artstream.
                    </div>
                    {/* Media description field (separate from Post text) */}
                    <div className="mt-3 space-y-2">
                      <label htmlFor="media-description" className="text-sm text-gray-300 font-medium">Media description</label>
                      <CustomTextarea
                        id="media-description"
                        value={postDescription}
                        onChange={(value) => setPostDescription(value.slice(0, 500))}
                        placeholder={'Add a description for your media…'}
                        maxLength={500}
                        autoComplete="off"
                        className="min-h-[100px] bg-black/30 border-slate-700/50 focus-visible:ring-purple-500 rounded-xl py-4 px-5 text-white placeholder-gray-500 hover:border-slate-600 transition-colors w-full"
                      />
                    </div>
                    {/* Action bar (media only) */}
                    <div className="mt-4 border border-slate-700/60 bg-black/20 rounded-xl px-3 py-2 flex items-center justify-between">
                      <span className="text-sm text-white/80">Add to your post</span>
                      <div className="flex items-center gap-1.5">
                        <TooltipProvider>
                          <Tooltip open={openIconTooltip === 'media'} onOpenChange={(o) => !o && setOpenIconTooltip(null)}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => { setOpenIconTooltip('media'); handleTriggerUpload(); }}
                                className={`p-2 rounded-full border transition-colors ${selectedImage ? 'border-purple-500/60 bg-purple-500/15 text-purple-200' : 'border-transparent hover:bg-white/10 text-white/80'}`}
                                aria-label="Add photo/video"
                              >
                                <ImageIcon className="w-5 h-5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-black/90 border-purple-500/30">
                              <div className="text-xs text-white/80">Add photo or video</div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5 italic">
                      FOMO tries to understand your content and suggests tags. Click tags to add them to your post.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="max-w-2xl mx-auto">
                  <div className="flex flex-col gap-5">
                    {/* Removed legacy destination indicator (Artstream/Loops). The flow is now simplified. */}

                    {selectedImage && (
                      <div className="space-y-2">
                        <label htmlFor="post-title" className="text-sm text-gray-300 font-medium">Media title</label>
                        <div className="relative">
                          <Input
                            id="post-title"
                            value={postTitle}
                            onChange={(e) => setPostTitle(e.target.value.slice(0, 30))}
                            placeholder="Add a short title for your media"
                            maxLength={30}
                            className="bg-black/30 border-slate-700/50 focus-visible:ring-purple-500 rounded-xl py-5 sm:py-6 text-white placeholder-gray-500 hover:border-slate-600 transition-colors"
                          />
                          <div className="absolute bottom-2 right-3 text-xs text-white/70">
                            {postTitle.length} / 30
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <label htmlFor="post-title" className="text-sm text-gray-300 font-medium">Title</label>
                      <div className="relative">
                        <div className="relative">
                          <MentionInput
                            ref={textareaRef}
                            value={postCopy}
                            onChange={(value) => setPostCopy(value.slice(0, 120))}
                            placeholder={"Add a title for your post"}
                            maxLength={120}
                            className="w-full"
                            textareaClassName="min-h-[48px] bg-black/30 border-slate-700/50 focus-visible:ring-purple-500 rounded-xl py-3 px-4 text-white placeholder-gray-500 hover:border-slate-600 transition-colors w-full font-medium"
                          />
                          
                          {/* Community Preview */}
                          <CommunityPreview 
                            communities={predictedCommunities} 
                            isExtracting={isExtractingCommunities}
                            className="mt-2"
                            disabledCommunities={disabledCommunities}
                            onToggleCommunity={handleToggleCommunity}
                          />
                          
                          {isAnalyzing && (
                            <div className="absolute top-2 right-2">
                              <div className="w-6 h-6 flex items-center justify-center bg-purple-900/40 rounded-full p-1">
                                <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                              </div>
                            </div>
                          )}
                          
                          {contextAnalysis && !isAnalyzing && (
                            <TooltipProvider>
                              <Tooltip open={showContextTooltip} onOpenChange={setShowContextTooltip}>
                                <TooltipTrigger asChild>
                                  <button 
                                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-purple-700/40 hover:bg-purple-700/60 rounded-full p-1 transition-colors"
                                    type="button"
                                    onClick={() => setShowContextTooltip(true)}
                                  >
                                    <Brain className="w-4 h-4 text-purple-300" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-xs bg-black/90 border-purple-500/30">
                                  <div className="space-y-2 p-1">
                                    <div className="flex items-center gap-2">
                                      <Sparkles className="w-4 h-4 text-purple-400" />
                                      <span className="text-sm font-medium text-purple-300">Context Analysis</span>
                                    </div>
                                    {contextAnalysis.emotionalState && (
                                      <div className="flex gap-1 flex-wrap">
                                        <span className="text-xs text-white/70">Emotional tone:</span>
                                        <span className="text-xs font-medium text-purple-300">{contextAnalysis.emotionalState}</span>
                                      </div>
                                    )}
                                    {contextAnalysis.interests && contextAnalysis.interests.length > 0 && (
                                      <div className="flex gap-1 flex-wrap">
                                        <span className="text-xs text-white/70">Key topics:</span>
                                        <span className="text-xs font-medium text-purple-300">{contextAnalysis.interests.slice(0, 3).join(', ')}</span>
                                      </div>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        
                        <div className="flex justify-between items-center mt-1">
                          <div className="text-xs text-white/70">
                            {postCopy.length} / 120
                          </div>
                          
                          {suggestedTags.length > 0 && (
                            <div className="flex items-center gap-1">
                              <Lightbulb className="w-3 h-3 text-amber-400" />
                              <span className="text-xs text-amber-400/80">Suggested tags:</span>
                            </div>
                          )}
                        </div>
                        
                        {suggestedTags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {suggestedTags.map((tag) => (
                              <Badge 
                                key={tag}
                                variant={selectedTags.includes(tag) ? "default" : "outline"}
                                className={`cursor-pointer px-2 py-0.5 text-xs hover:bg-purple-700/70 ${selectedTags.includes(tag) ? 'bg-purple-700' : 'bg-transparent hover:bg-purple-900/30 border-purple-700/50 text-purple-300'} transition-colors`}
                                onClick={() => {
                                  if (selectedTags.includes(tag)) {
                                    setSelectedTags(selectedTags.filter(t => t !== tag));
                                    setPostCopy(postCopy.replace(`#${tag}`, '').trim());
                                  } else {
                                    setSelectedTags([...selectedTags, tag]);
                                    setPostCopy(`${postCopy} #${tag}`.trim());
                                  }
                                }}
                              >
                                #{tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Description field */}
                    <div className="space-y-2">
                      <label htmlFor="post-description" className="text-sm text-gray-300 font-medium">Description</label>
                      <CustomTextarea
                        id="post-description"
                        value={postDescription}
                        onChange={(value) => setPostDescription(value.slice(0, 500))}
                        placeholder={'Add a description…'}
                        maxLength={500}
                        autoComplete="off"
                        className="min-h-[120px] bg-black/30 border-slate-700/50 focus-visible:ring-purple-500 rounded-xl py-4 px-5 text-white placeholder-gray-500 hover:border-slate-600 transition-colors w-full"
                      />
                    </div>
                    
                    <div>
                        {/* Action bar: media only */}
                        <div className="mt-4 border border-slate-700/60 bg-black/20 rounded-xl px-3 py-2 flex items-center justify-between">
                          <span className="text-sm text-white/80">Add to your post</span>
                          <div className="flex items-center gap-1.5">
                            <TooltipProvider>
                              <Tooltip open={openIconTooltip === 'media'} onOpenChange={(o) => !o && setOpenIconTooltip(null)}>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={(e) => { setOpenIconTooltip('media'); handleTriggerUpload(); }}
                                    className={`p-2 rounded-full border transition-colors ${selectedImage ? 'border-purple-500/60 bg-purple-500/15 text-purple-200' : 'border-transparent hover:bg-white/10 text-white/80'}`}
                                    aria-label="Add photo/video"
                                  >
                                    <ImageIcon className="w-5 h-5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-black/90 border-purple-500/30">
                                  <div className="text-xs text-white/80">Add photo or video</div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                    </div>
                  </div>
                </div>
              )}
            </main>
            <footer className="p-5 border-t border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-900/95 sticky bottom-0">
              <div className="flex justify-between items-center max-w-4xl mx-auto">
                <Button 
                  variant="outline" 
                  onClick={handleBack}
                  className="border-slate-700 hover:bg-slate-800 hover:text-white transition-colors"
                >
                  {selectedImage ? 'Remove media' : 'Cancel'}
                </Button>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-3">
                    <Switch 
                      checked={isArt}
                      onCheckedChange={setIsArt}
                      id="is-art-toggle"
                    />
                    <label htmlFor="is-art-toggle" className="text-sm text-white/70 cursor-pointer">
                      Is it art?
                    </label>
                  </div>
                  <Button 
                    onClick={handleSubmit} 
                    disabled={isSubmitDisabled}
                    className={`bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 ${isSubmitDisabled ? 'opacity-50' : 'opacity-100'} transition-all duration-300`}
                  >
                    Post
                  </Button>
                </div>
              </div>
            </footer>
          </>
        );
      case 'uploading':
        return (
          <>
            <header className="p-5 border-b border-slate-700/80 flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-800/95">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-6 h-6 bg-purple-500 flex items-center justify-center rounded-full text-sm">3</span> 
                <span>Uploading</span>
              </h2>
            </header>
            <main className="flex-1 p-6 overflow-y-auto bg-gradient-to-b from-slate-800/80 to-slate-900/90 flex flex-col items-center justify-center">
              <div className="w-full max-w-lg bg-black/30 backdrop-blur rounded-2xl p-8 border border-slate-700/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 z-0"></div>
                <div className="relative z-10">
                  {uploadProgress && <PostUploadProgress progress={uploadProgress.progress} state={uploadProgress.state} />}
                  
                  {uploadProgress && uploadProgress.state === 'complete' && (
                    <div className="mt-6 text-center">
                      <p className="text-green-400 font-medium mb-4">Upload complete!</p>
                      <p className="text-sm text-gray-300">Your post is now visible to others</p>
                    </div>
                  )}
                  
                  {uploadProgress && uploadProgress.state === 'error' && (
                    <div className="mt-6 text-center">
                      <p className="text-red-400 font-medium mb-4">Upload failed</p>
                      <p className="text-sm text-gray-300">Please try again later</p>
                    </div>
                  )}
                </div>
              </div>
            </main>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Portal>
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
        onClick={handleClose}
        onMouseDown={(e) => e.target === e.currentTarget && e.preventDefault()}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="bg-card w-full max-w-lg rounded-2xl shadow-xl overflow-hidden flex flex-col h-full max-h-[80vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {renderStep()}
        </motion.div>
      </div>
    </Portal>
  );
};

export default PostPromptModal;
